import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { Driver, FilterOptions, SortType } from '../../types/models';
import { DriverState } from '../../types/store';
import { database, DB_PATHS } from '../../config/firebase';
import { ref, get, onValue } from 'firebase/database';
import type { AppDispatch } from '../index';

// driversm2 테이블에서 드라이버 목록을 가져오는 thunk (1회성)
export const fetchDrivers = createAsyncThunk<Driver[]>(
  'driver/fetchDrivers',
  async () => {
    const snapshot = await get(ref(database, DB_PATHS.DRIVERS));
    const data = snapshot.val();
    if (!data) return [];
    // key가 id, value가 Driver 객체
    return Object.entries(data).map(([id, value]) => ({
      id,
      ...(value as Omit<Driver, 'id'>),
    }));
  }
);

// driversm2 테이블 실시간 동기화 thunk
export const fetchDriversRealtime = () => (dispatch: AppDispatch) => {
  const dbRef = ref(database, DB_PATHS.DRIVERS);
  const unsubscribe = onValue(dbRef, (snapshot) => {
    const data = snapshot.val();
    const drivers = data
      ? Object.entries(data).map(([id, value]) => ({
          id,
          ...(value as Omit<Driver, 'id'>),
        }))
      : [];
    dispatch(setDrivers(drivers));
  });
  return unsubscribe;
};

const initialState: DriverState = {
  items: [],
  selectedDriver: null,
  loading: false,
  error: null,
  filters: {},
  sort: 'expire_desc'
};

const driverSlice = createSlice({
  name: 'driver',
  initialState,
  reducers: {
    setDrivers: (state, action: PayloadAction<Driver[]>) => {
      state.items = action.payload;
    },
    setSelectedDriver: (state, action: PayloadAction<Driver | null>) => {
      state.selectedDriver = action.payload;
    },
    setFilters: (state, action: PayloadAction<FilterOptions>) => {
      state.filters = action.payload;
    },
    setSort: (state, action: PayloadAction<SortType>) => {
      state.sort = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDrivers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDrivers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchDrivers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '드라이버 목록을 불러오지 못했습니다.';
      });
  }
});

export const { setDrivers, setSelectedDriver, setFilters, setSort } = driverSlice.actions;
export default driverSlice.reducer; 